<template>
  <div class="kanban-board flex h-full gap-3 overflow-x-auto p-4">
    <div
      v-if="isFetching"
      class="flex h-full w-full items-center justify-center"
    >
      <span class="text-sm text-n-slate-11">Carregando...</span>
    </div>

    <template v-else>
      <!-- Coluna para conversas sem estágio -->
      <KanbanColumn
        :stage="unstagedColumn"
        :conversations="unstagedConversations"
        @card-click="onCardClick"
        @card-moved="onCardMoved"
      />

      <KanbanColumn
        v-for="stage in stages"
        :key="stage.id"
        :stage="stage"
        :conversations="conversationsByStage[stage.id] || []"
        @card-click="onCardClick"
        @card-moved="onCardMoved"
      />
    </template>
  </div>
</template>

<script>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import KanbanColumn from './KanbanColumn.vue';
import { useAccount } from 'dashboard/composables/useAccount';

export default {
  name: 'KanbanBoard',
  components: {
    KanbanColumn,
  },
  emits: ['conversation-click'],
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { accountId } = useAccount();

    const isFetching = ref(true);
    const allConversations = ref([]);

    const unstagedColumn = {
      id: null,
      name: 'Sem Estágio',
      color: '#94A3B8',
      position: -1,
    };

    const stages = computed(() => store.getters['kanbanStages/getStages']);

    const conversationsByStage = computed(() => {
      const map = {};
      stages.value.forEach(s => { map[s.id] = []; });
      allConversations.value.forEach(conv => {
        const stageId = conv.kanban_stage_id;
        if (stageId && map[stageId]) {
          map[stageId].push(conv);
        }
      });
      return map;
    });

    const unstagedConversations = computed(() => {
      const stageIds = stages.value.map(s => s.id);
      return allConversations.value.filter(
        conv => !conv.kanban_stage_id || !stageIds.includes(conv.kanban_stage_id)
      );
    });

    async function fetchConversations() {
      isFetching.value = true;
      try {
        const params = {
          assignee_type: 'all',
          status: 'open',
          sort_by: 'last_activity_at_desc',
          page: 1,
        };
        const { data } = await axios.get(
          `/api/v1/accounts/${accountId.value}/conversations`,
          { params }
        );
        allConversations.value = data?.data?.payload || [];

        // Fetch more pages if needed
        const totalCount = data?.data?.meta?.all_count || 0;
        const perPage = 25;
        const totalPages = Math.ceil(totalCount / perPage);
        for (let page = 2; page <= Math.min(totalPages, 10); page++) {
          const res = await axios.get(
            `/api/v1/accounts/${accountId.value}/conversations`,
            { params: { ...params, page } }
          );
          const moreConvs = res.data?.data?.payload || [];
          allConversations.value = [...allConversations.value, ...moreConvs];
        }
      } catch (err) {
        console.error('Kanban: failed to fetch conversations', err);
      } finally {
        isFetching.value = false;
      }
    }

    async function onCardMoved({ conversation, stageId }) {
      try {
        await axios.patch(
          `/api/v1/accounts/${accountId.value}/conversations/${conversation.id}`,
          { kanban_stage_id: stageId }
        );
        // Update local state
        const idx = allConversations.value.findIndex(c => c.id === conversation.id);
        if (idx !== -1) {
          allConversations.value[idx] = {
            ...allConversations.value[idx],
            kanban_stage_id: stageId,
          };
        }
      } catch (err) {
        console.error('Kanban: failed to move card', err);
        fetchConversations();
      }
    }

    function onCardClick(conversation) {
      const convId = conversation.display_id || conversation.id;
      router.push({
        name: 'inbox_conversation',
        params: { accountId: accountId.value, conversation_id: convId },
      });
    }

    onMounted(async () => {
      await store.dispatch('kanbanStages/get');
      await fetchConversations();
    });

    return {
      isFetching,
      stages,
      unstagedColumn,
      unstagedConversations,
      conversationsByStage,
      onCardMoved,
      onCardClick,
    };
  },
};
</script>

<style scoped>
.kanban-board {
  min-height: calc(100vh - 64px);
}
</style>
